import { useScrollAnimation } from "../../../hooks/useScrollAnimation";

const metrics = [
  {
    id: 1,
    value: "50%",
    label: "Faster Approval Processing",
    description:
      "Reduce approval turnaround time with automated routing, notifications, and status tracking.",
    icon: "ri-time-line",
  },
  {
    id: 2,
    value: "200+",
    label: "Hours Saved Annually",
    description:
      "Minimize repetitive manual operations across approvals, assets, logistics, and admin workflows.",
    icon: "ri-line-chart-line",
  },
  {
    id: 3,
    value: "95%",
    label: "Asset Tracking Accuracy",
    description:
      "Keep devices, vehicles, and workplace assets organized with real-time visibility and lifecycle management.",
    icon: "ri-shield-check-line",
  },
];

const highlights = [
  "End-to-end workflow visibility",
  "Centralized request and asset operations",
  "Fewer manual errors and delays",
  "Scalable system for growing teams",
];

export default function TestimonialsSection() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-24 bg-slate-900" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div
          className={`text-center mb-14 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-3 block">
            Performance & Impact
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Measurable Results for Modern Operations
          </h2>
          <p className="text-slate-400 text-base max-w-2xl mx-auto leading-relaxed">
            BizLab helps organizations streamline internal workflows, reduce
            operational overhead, and gain full visibility across workplace
            management.
          </p>
        </div>

        {/* Metric cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {metrics.map((metric, index) => (
            <div
              key={metric.id}
              className={`rounded-2xl border border-white/10 bg-white/5 p-8 hover:bg-white/[0.07] hover:border-blue-400/30 transition-all duration-300 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${index * 100 + 100}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-400/20 flex items-center justify-center mb-6">
                <i className={`${metric.icon} text-blue-400 text-xl`} />
              </div>

              <div className="text-4xl md:text-5xl font-bold text-white mb-3">
                {metric.value}
              </div>

              <h3 className="text-lg font-semibold text-white mb-3">
                {metric.label}
              </h3>

              <p className="text-sm leading-relaxed text-slate-400">
                {metric.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom summary panel */}
        <div
          className={`rounded-3xl border border-white/10 bg-gradient-to-r from-white/5 to-white/[0.03] p-8 md:p-10 transition-all duration-700 delay-500 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-10 items-start">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-blue-400 block mb-3">
                Operational Value
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Built to Improve Efficiency Across Every Team
              </h3>
              <p className="text-slate-400 leading-relaxed max-w-2xl">
                From approval workflows to asset tracking and workplace support,
                BizLab replaces fragmented manual processes with one unified
                operating system for internal operations.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {highlights.map((item, index) => (
                <div
                  key={item}
                  className="flex items-start gap-3 rounded-xl border border-white/10 bg-slate-800/50 px-4 py-4"
                >
                  <div className="w-6 h-6 rounded-full bg-blue-400/15 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-check-line text-blue-400 text-sm" />
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}